import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import { getMe } from 'Douleutaras/redux/data/users/meSlice';
import { mapServicePriceSettings } from 'Douleutaras/views/seller-dashboard/settings/Profile/EditServices/servicePriceSettingsMapper';
import {
  isItemResource,
  isPriceResource,
  mapCatalogLines
} from 'Douleutaras/views/seller-dashboard/settings/Profile/EditServices/servicePriceCatalog';
import {
  CatalogLine,
  SettingsIncludedResource,
  SettingsServicePriceResource,
  SettingsServicePricesDocument,
  SettingsServicesCategory
} from 'Douleutaras/views/seller-dashboard/settings/Profile/EditServices/types';

// Reads the pro from the JWT; prices, catalog items and level labels are included by default
export const SERVICE_PRICE_SETTINGS_ENDPOINT = 'api/public/v3/user/settings/service-prices/';
export const SERVICE_PRICES_WRITE_ENDPOINT = `${SERVICE_PRICE_SETTINGS_ENDPOINT}prices/`;

/** A write's answer: the row, and the levels its title is built from. */
export interface ServicePriceWriteResult {
  /** Absent when a 2xx came back without the row; the list is refetched instead. */
  price?: SettingsServicePriceResource;
  included: Array<SettingsIncludedResource>;
}

export interface CreateServicePriceArg {
  /** Where the row goes, should the answer not link it. */
  categoryId: string;
  itemId: string;
  body: unknown;
}

export interface ServicePriceWriteError {
  /** The BE's field-keyed messages; absent when the request failed for another reason. */
  errors?: Record<string, unknown>;
  status?: number;
}

interface WriteThunkConfig {
  state: RootState;
  rejectValue: ServicePriceWriteError;
}

// tslint:disable-next-line:no-any
const rejectFromResponse = (api: any, response: any) => {
  const errorResponse = response?.payload?.response;
  const errors = errorResponse?.status === 400 ? errorResponse.data?.errors : undefined;

  return api.rejectWithValue({ errors, status: errorResponse?.status });
};

const isSuccess = (status?: number): boolean => status !== undefined && status >= 200 && status < 300;

// tslint:disable-next-line:no-any
const toWriteResult = (api: any, response: any): ServicePriceWriteResult => {
  if (!isSuccess(response?.status)) {
    return rejectFromResponse(api, response);
  }
  // `user/me` carries the unpriced count every write may change
  api.dispatch(getMe());

  const doc = response.data;
  if (!doc?.data || !isPriceResource(doc.data)) {
    // Saved, but nothing to patch the list with
    api.dispatch(getServicePriceSettings());

    return { included: [] };
  }

  return { price: doc.data, included: doc.included ?? [] };
};

export const getServicePriceSettings = createAsyncThunk<SettingsServicePricesDocument, void, { state: RootState }>(
  'settings/servicePrices/get',
  async (_, api) => {
    const response = await makeRequest(SERVICE_PRICE_SETTINGS_ENDPOINT, { method: 'GET' })(api);

    const doc = response?.data as SettingsServicePricesDocument | undefined;
    if (!doc || !Array.isArray(doc.data)) {
      throw new Error('Malformed service-price settings response');
    }

    return doc;
  }
);

export const createServicePrice = createAsyncThunk<ServicePriceWriteResult, CreateServicePriceArg, WriteThunkConfig>(
  'settings/servicePrices/create',
  async ({ body }, api) => toWriteResult(api, await makeRequest(SERVICE_PRICES_WRITE_ENDPOINT, { method: 'POST', body })(api))
);

export const updateServicePrice = createAsyncThunk<ServicePriceWriteResult, { priceId: string; body: unknown }, WriteThunkConfig>(
  'settings/servicePrices/update',
  async ({ priceId, body }, api) =>
    toWriteResult(api, await makeRequest(`${SERVICE_PRICES_WRITE_ENDPOINT}${priceId}/`, { method: 'PATCH', body })(api))
);

export const deleteServicePrice = createAsyncThunk<string, string, WriteThunkConfig>(
  'settings/servicePrices/delete',
  async (priceId, api) => {
    const response = await makeRequest(`${SERVICE_PRICES_WRITE_ENDPOINT}${priceId}/`, { method: 'DELETE' })(api);
    // Already gone, e.g. deleted in another tab
    const isGone = response?.payload?.response?.status === 404;
    if (!isSuccess(response?.status) && !isGone) {
      return rejectFromResponse(api, response);
    }
    api.dispatch(getMe());

    return priceId;
  }
);

export interface ServicePriceSettingsState {
  document?: SettingsServicePricesDocument;
  loading: boolean;
  error: boolean;
  // Set once a list has arrived, so a refetch keeps it on screen
  loaded: boolean;
}

const initialState: ServicePriceSettingsState = {
  loading: false,
  error: false,
  loaded: false
};

const sameResource = (a: { type: string; id: string }, b: { type: string; id: string }): boolean =>
  a.type === b.type && a.id === b.id;

/** Adds the resources a write answered with, replacing any already held. */
const mergeIncluded = (doc: SettingsServicePricesDocument, resources: Array<SettingsIncludedResource>): void => {
  doc.included = [
    ...(doc.included ?? []).filter(held => !resources.some(resource => sameResource(held, resource))),
    ...resources
  ];
};

/** Points the catalog line at the pro's row on it, or back at none. */
const linkItemPrice = (doc: SettingsServicePricesDocument, itemId: string | undefined, priceId?: string): void => {
  const item = (doc.included ?? []).find(resource => isItemResource(resource) && resource.id === itemId);
  if (item && isItemResource(item)) {
    item.relationships = {
      ...item.relationships,
      // tslint:disable-next-line:no-null-keyword
      price: { data: priceId ? { type: 'seller-service-price', id: priceId } : null }
    };
  }
};

export const servicePriceSettingsSlice = createSlice({
  name: 'servicePriceSettings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getServicePriceSettings.pending, state => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getServicePriceSettings.fulfilled, (state, action) => {
      state.document = action.payload;
      state.loading = false;
      state.loaded = true;
    });
    builder.addCase(getServicePriceSettings.rejected, state => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(createServicePrice.fulfilled, (state, { payload: { price, included }, meta: { arg } }) => {
      const doc = state.document;
      const categoryId = price?.relationships?.category?.data?.id ?? arg.categoryId;
      const category = doc?.data.find(resource => resource.id === categoryId);
      if (!doc || !category || !price) {
        return;
      }
      mergeIncluded(doc, [price, ...included]);
      category.attributes.empty = false;
      category.relationships = {
        ...category.relationships,
        prices: { data: [...(category.relationships?.prices?.data ?? []), { type: price.type, id: price.id }] }
      };
      linkItemPrice(doc, price.relationships?.item?.data?.id ?? arg.itemId, price.id);
    });
    builder.addCase(updateServicePrice.fulfilled, (state, { payload: { price, included } }) => {
      if (state.document && price) {
        mergeIncluded(state.document, [price, ...included]);
      }
    });
    builder.addCase(deleteServicePrice.fulfilled, (state, { payload: priceId }) => {
      const doc = state.document;
      const price = doc?.included?.find(resource => isPriceResource(resource) && resource.id === priceId);
      if (!doc || !price || !isPriceResource(price)) {
        return;
      }
      doc.included = (doc.included ?? []).filter(resource => !sameResource(resource, price));
      doc.data.forEach(category => {
        const prices = (category.relationships?.prices?.data ?? []).filter(link => link.id !== priceId);
        if (category.relationships?.prices) {
          category.relationships.prices.data = prices;
          category.attributes.empty = prices.length === 0;
        }
      });
      linkItemPrice(doc, price.relationships?.item?.data?.id);
    });
  }
});

export const selectServicePriceSettings = (state: RootState): ServicePriceSettingsState => state.servicePriceSettings;

const selectDocument = (state: RootState): SettingsServicePricesDocument | undefined => state.servicePriceSettings.document;

export const selectServicePriceCategories = createSelector(
  selectDocument,
  (doc): Array<SettingsServicesCategory> => doc ? mapServicePriceSettings(doc) : []
);

export const selectServicePriceCatalog = createSelector(
  selectDocument,
  (doc): Array<CatalogLine> => doc ? mapCatalogLines(doc) : []
);

export const selectServicePriceById = (state: RootState, priceId: string): SettingsServicePriceResource | undefined => {
  const included = selectDocument(state)?.included ?? [];
  const price = included.find(resource => resource.id === priceId && isPriceResource(resource));

  return price && isPriceResource(price) ? price : undefined;
};

export default servicePriceSettingsSlice.reducer;
